import React, { useEffect } from 'react';
import { useTexture } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * NeighborhoodBackdrop with 2 layers:
 * - foregroundUrl (main neighborhood image)
 * - backgroundUrl (farther layer, e.g. neighborhoodImage2.png)
 */
export function NeighborhoodBackdrop({
  // Foreground (closer)
  x = 45,
  y = 3.75,
  z = 0,
  width = 50,
  height = 6,
  rotationY = -1.45,
  foregroundUrl = '/stageAssets/stage2/scene/neighborhoodImage.png',

  // Background (farther)
  bgX = 48,
  bgY = 3.25,
  bgZ = -4.5, // push it slightly "behind" the foreground plane
  bgWidth = 37,
  bgHeight = 6,
  bgRotationY = -1.5,
  backgroundUrl = '/stageAssets/stage2/scene/neighborhoodImage2.png',

  // Render ordering (more negative draws earlier / behind)
  bgRenderOrder = -10,
  fgRenderOrder = -5,
}) {
  const { gl } = useThree();
  const [fgTexture, bgTexture] = useTexture([foregroundUrl, backgroundUrl]);

  useEffect(() => {
    const maxAniso = gl.capabilities.getMaxAnisotropy();

    const prep = (tex) => {
      if (!tex) return;

      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;

      tex.anisotropy = maxAniso;

      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;

      tex.needsUpdate = true;
    };

    prep(fgTexture);
    prep(bgTexture);
  }, [gl, fgTexture, bgTexture]);

  return (
    <group>
      {/* Background layer */}
      <mesh
        position={[bgX, bgY, bgZ]}
        rotation={[0, bgRotationY, 0]}
        renderOrder={bgRenderOrder}
      >
        <planeGeometry args={[bgWidth, bgHeight]} />
        <meshBasicMaterial
          map={bgTexture}
          transparent
          depthWrite={false}
          depthTest={true}
          alphaTest={0.5}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Foreground layer */}
      <mesh
        position={[x, y, z]}
        rotation={[0, rotationY, 0]}
        renderOrder={fgRenderOrder}
      >
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial
          map={fgTexture}
          transparent
          depthWrite={false}
          depthTest={true}
          alphaTest={0.5}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}
